import * as React from "react";
import { useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { ChevronDownIcon, Loader2 } from "lucide-react";
import { useCachedOptions } from "@/hooks/useCachedOptions";
import SelectedValueChips from "./SelectedValueChips";

interface MultiSelectFromAPIProps {
  optionsApiUrl: string; // API endpoint to fetch options
  onValueChange: (value: string[]) => void;
  defaultValue?: string[];
  placeholder?: string;
  animation?: number;
  maxCount?: number;
  modalPopover?: boolean;
  asChild?: boolean;
  className?: string;
  showSelectAll?: boolean;
  showSearch?: boolean;
  showActionButton?: boolean;
  queryParamName?: string; // query string param, defaults to 'query'
  perPage?: number; // defaults to 20
  labelFormatter?: (option: any) => string; // Custom function to format the label
}

const PER_PAGE_DEFAULT = 20;

export const MultiSelectFromAPI: React.FC<MultiSelectFromAPIProps> = ({
  optionsApiUrl,
  onValueChange,
  defaultValue = [],
  placeholder = "Select options",
  showSearch = false,
  queryParamName = "query",
  perPage = PER_PAGE_DEFAULT,
  modalPopover = false,
  labelFormatter,
}) => {
  const [selected, setSelected] = useState<string[]>(defaultValue);
  const [open, setOpen] = useState(false);

  // Persisted options
  const { options, isLoading, hasMore, loadOptions, loadNext, abortRequests } = useCachedOptions(optionsApiUrl, queryParamName, perPage, optionsApiUrl);
  // When popover opens first time, fetch page 1
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen && options.length === 0) {
      loadOptions("", true);
    } else if (!isOpen) {
      // Abort any ongoing API calls when dropdown closes
      abortRequests();
    }
    setOpen(isOpen);
  };

  const handleSearch = (q: string) => {
    // The hook will automatically abort previous requests and start new one
    loadOptions(q, true);
  };

  const handleToggle = (value: string) => {
    setSelected((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    onValueChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  const handleRemoveChip = (value: string) => {
    setSelected((prev) => prev.filter((v) => v !== value));
    onValueChange(selected.filter((v) => v !== value));
  };

  // Intersection Observer for infinite scroll
  const lastOptionRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (options.length > 0 && hasMore && lastOptionRef.current) {
      const observer = new window.IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            loadNext();
          }
        },
        { threshold: 1 }
      );
      observer.observe(lastOptionRef.current);
      return () => observer.disconnect();
    }
  }, [options.length, hasMore]);

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={modalPopover}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-between !bg-white min-h-8 h-auto !py-0 !px-2.5">
          <div className="text-left">
            {selected.length > 0 ? (
              <SelectedValueChips selectedValues={selected} options={options} onRemove={handleRemoveChip} maxVisible={2} className="justify-start" labelFormatter={labelFormatter} />
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </div>
          {isLoading ? <Loader2 className="animate-spin size-5 ml-2" /> : <ChevronDownIcon className={`h-4 w-4 ml-2 ${open ? "rotate-180" : ""}`} />}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <Command>
          {showSearch && <CommandInput placeholder="Search..." onValueChange={handleSearch} className="" />}
          <CommandList>
            <CommandEmpty className="text-13 py-4 px-3 flex justify-center">{isLoading ? "Loading..." : "Options not found."}</CommandEmpty>
            <CommandGroup>
              {options.map((opt, idx) => {
                const isLast = idx === options.length - 1;
                const isSelected = selected.includes(opt.value);
                return (
                  <CommandItem
                    key={opt.value}
                    ref={isLast && hasMore ? lastOptionRef : undefined}
                    onSelect={() => handleToggle(opt.value)}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <div className="relative w-4 h-4">
                      <input
                        id={`checkbox-${opt.value}`}
                        type="checkbox"
                        checked={isSelected}
                        readOnly
                        className="peer absolute opacity-0 w-full h-full hover:cursor-pointer"
                      />
                      <label
                        htmlFor={`checkbox-${opt.value}`}
                        className="block w-4 h-4 rounded border cursor-pointer transition-all duration-200 active:scale-105"
                        style={{ borderColor: "#6F36BC" }}
                      >
                        <svg className="pointer-events-none p-[2px]" viewBox="0 0 24 24">
                          <path
                            d="M4 12l6 6L20 6"
                            className="stroke-[4] fill-none stroke-linecap-round stroke-linejoin-round transition-all duration-300 ease-[cubic-bezier(1,0,0.37,0.91)]"
                            style={{
                              stroke: "#6F36BC",
                              strokeDasharray: 80,
                              strokeDashoffset: isSelected ? 0 : 81,
                            }}
                          />
                        </svg>
                      </label>
                    </div>
                    {opt.iconUrl ? (
                      <img src={opt.iconUrl} alt={opt.label} className={`rounded-full object-cover w-6 h-6 `} />
                    ) : opt.iconText ? (
                      <div className="min-w-[24px] h-6 border border-text-primary rounded-full text-text-primary flex items-center justify-center text-10 leading-normal font-medium">
                        {opt.iconText}
                      </div>
                    ) : null}
                    <span className="truncate">{labelFormatter ? labelFormatter(opt) : opt.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
